import * as Sentry from '@sentry/vue';
import type { App } from 'vue';
import {
  sanitizeForTelemetry,
  sanitizeUrlForTelemetry,
} from '@/services/errorParser';

// entr-frontend project DSN. Used when VITE_SENTRY_DSN is not injected at build
// (CI builds). Development hosts get NO dsn at all — dev events used to reach
// the production project and flap its alerts; set VITE_SENTRY_FORCE=true to
// deliberately re-enable sending while debugging the Sentry integration.
const FALLBACK_DSN =
  'https://70c11366a405e943bef966889217b841@o4506991194996736.ingest.us.sentry.io/4511630092402688';

export function resolveSentryDsn(
  environment: string,
  force: boolean
): string | undefined {
  if (environment === 'development' && !force) return undefined;
  return import.meta.env.VITE_SENTRY_DSN || FALLBACK_DSN;
}

// Open-record cast: VITE_SENTRY_RELEASE is injected by the CI build but
// is not declared in the ImportMetaEnv interface, and adding it there is
// out of scope for this change. Falls back to a dev label so local
// `npm run dev` works without a CI-style build step.
const RELEASE =
  (import.meta.env as Record<string, string | undefined>).VITE_SENTRY_RELEASE ||
  'entr-frontend@dev';

// Read MODE at each initSentry() call (not module-eval time) so vitest
// suites that stub `import.meta.env.MODE` see the change. Frontend's
// pattern relies on tests never importing init.ts; ENTR's main.ts wires
// Sentry on every boot so the equivalent guard has to live inside
// initSentry itself.
function isTestMode(): boolean {
  return import.meta.env.MODE === 'test';
}

// The DSN is baked into the bundle, so a Playwright run ships into the real
// production project just by loading the page. isTestMode() only catches vitest
// (MODE === 'test'); an e2e run is MODE === 'development', which is how CI came
// to be 62% of this project's span volume. CI declares itself via
// VITE_SENTRY_ENVIRONMENT — a hostname can't tell a workflow runner from a
// developer's laptop, since both sit on 127.0.0.1.
function isCi(): boolean {
  return getEnvironment() === 'ci';
}

let initialized = false;
let hasActiveSession = false;

type SessionStatus = 'ok' | 'crashed' | 'abnormal' | 'exited';

export function initSentry(app: App): void {
  if (isTestMode() || isCi()) return;
  const environment = getEnvironment();
  Sentry.init({
    app,
    dsn: resolveSentryDsn(
      environment,
      (import.meta.env as Record<string, string | undefined>)
        .VITE_SENTRY_FORCE === 'true'
    ),
    environment,
    release: RELEASE,
    // Without browserTracingIntegration the SDK emits no transactions AND
    // attaches no sentry-trace/baggage to outgoing fetches — the backend
    // could never join ENTR sessions into a distributed trace.
    integrations: [Sentry.browserTracingIntegration()],
    // The browser is the trace head and the backend inherits its decision,
    // so this rate is a multiplier on backend span ingest for every ENTR
    // request — not a knob scoped to this app's own transactions. 1.0 pinned
    // the backend to 100% tracing for ENTR traffic regardless of the
    // backend's own rate. 0.1 matches the other kiosk apps.
    tracesSampleRate: 0.1,
    // Own hosts only. The direct-ENTR Azure transport
    // (*.azurewebsites.net) must never receive our trace headers.
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.theartisan\.ai/,
      /^\/api\//,
    ],
    beforeSend,
    beforeBreadcrumb,
  });
  initialized = true;
  safe('setTag:app', () => Sentry.setTag('app', 'entr-kiosk'));
}

export function setSentryScreen(screen: string): void {
  safe('setSentryScreen', () => Sentry.setTag('screen', screen));
}

export function setSentrySession(input: {
  sessionId?: string;
  groupId?: string;
  source?: string;
  photoCount?: number;
  voucherKind?: string;
}): void {
  safe('setSentrySession', () => {
    if (input.sessionId) {
      Sentry.setTag('session_id', input.sessionId);
      Sentry.setUser({ id: input.sessionId });
    }
    if (input.groupId) Sentry.setTag('group_id', input.groupId);
    if (input.source) Sentry.setTag('session_source', input.source);
    Sentry.setContext('entr_session', {
      session_id: input.sessionId ?? null,
      group_id: input.groupId ?? null,
      source: input.source ?? null,
      photo_count: input.photoCount ?? null,
      voucher_kind: input.voucherKind ?? null,
    });
  });
}

export function startSentrySession(input: {
  sessionId: string;
  groupId: string;
  source: string;
  photoCount: number;
  voucherKind: string;
}): void {
  setSentrySession(input);
  safe('startSentrySession', () => {
    if (hasActiveSession) {
      Sentry.captureSession(true);
      Sentry.endSession();
    }
    Sentry.startSession({
      ignoreDuration: false,
      release: RELEASE,
      environment: getEnvironment(),
    });
    Sentry.captureSession(false);
    hasActiveSession = true;
  });
  addBreadcrumb('entr.session', 'Session started', {
    session_id: input.sessionId,
    group_id: input.groupId,
    source: input.source,
    photo_count: input.photoCount,
    voucher_kind: input.voucherKind,
  });
}

export function endSentrySession(status: SessionStatus = 'exited'): void {
  safe('endSentrySession', () => {
    if (!hasActiveSession) return;
    const scope = Sentry.getCurrentScope() as Sentry.Scope & {
      getSession?: () => { status: SessionStatus } | undefined;
    };
    const currentSession = scope.getSession?.();
    if (currentSession) currentSession.status = status;
    Sentry.captureSession(true);
    Sentry.endSession();
    hasActiveSession = false;
  });
  addBreadcrumb('entr.session', 'Session ended', { status });
}

export function clearSentrySession(): void {
  safe('clearSentrySession', () => {
    Sentry.setUser(null);
    Sentry.setTag('session_id', '');
    Sentry.setTag('group_id', '');
    Sentry.setTag('session_source', '');
    Sentry.setContext('entr_session', null);
  });
}

export function setSentryLocale(locale: string): void {
  safe('setSentryLocale', () => Sentry.setTag('locale', locale));
}

// Tag names must match frontend/src/services/telemetry/sentry/context.ts
// setKioskContext() so a single Sentry dashboard filter (e.g.
// `kiosk_partner:fever`) lights up both fleets. Partner/country/platform
// are optional because ENTR currently has a single deployment and the
// values are baked at build time; once we have >1 ENTR partner this
// will start carrying real values from the boot payload.
export function setSentryKiosk(input: {
  slug?: string | null;
  kioskId?: string | null;
  apiKey?: string | null;
  partner?: string | null;
  country?: string | null;
  platform?: string | null;
}): void {
  safe('setSentryKiosk', () => {
    if (input.slug) Sentry.setTag('kiosk_slug', input.slug);
    if (input.kioskId) Sentry.setTag('kiosk_id', input.kioskId);
    if (input.apiKey) Sentry.setTag('kiosk_api_key', input.apiKey);
    if (input.partner) Sentry.setTag('kiosk_partner', input.partner);
    if (input.country) Sentry.setTag('country', input.country);
    if (input.platform) Sentry.setTag('platform', input.platform);
    Sentry.setContext('kiosk', {
      slug: input.slug ?? null,
      kiosk_id: input.kioskId ?? null,
      partner: input.partner ?? null,
      country: input.country ?? null,
      platform: input.platform ?? null,
      // Kiosk API keys are internal machine identifiers stored and matched in
      // Postgres. Keep the full value in Sentry so launch URLs, backend auth,
      // and kiosk config rows can be correlated without guessing.
      api_key: input.apiKey ?? null,
    });
  });
}

export function addBreadcrumb(
  category: string,
  message: string,
  data: Record<string, unknown> = {}
): void {
  safe('addBreadcrumb', () =>
    Sentry.addBreadcrumb({
      category,
      message,
      data: sanitizeForTelemetry(data) as Record<string, unknown>,
      level: 'info',
    })
  );
}

export function captureException(error: unknown, context = 'entr'): void {
  safe('captureException', () =>
    Sentry.captureException(error, {
      tags: {
        app: 'entr-kiosk',
        context,
      },
    })
  );
}

export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  extra: Record<string, unknown> = {}
): void {
  safe('captureMessage', () =>
    Sentry.captureMessage(message, {
      level,
      tags: { app: 'entr-kiosk' },
      extra: sanitizeForTelemetry(extra) as Record<string, unknown>,
    })
  );
}

export function flushSentry(timeoutMs = 2000): Promise<boolean> {
  return (
    safe('flushSentry', () => Sentry.flush(timeoutMs)) ?? Promise.resolve(false)
  );
}

export function withSpan<T>(
  name: string,
  op: string,
  attributes: Record<string, string>,
  fn: () => Promise<T>
): Promise<T> {
  return Sentry.startSpan({ name, op, attributes }, fn);
}

// Manual sentry-trace/baggage for calls made inside withSpan — propagation
// that is assertable and independent of browserTracingIntegration's fetch
// patching (mirrors the B2C frontend's getTraceHeaders, including its
// defensive length caps against CDN/proxy header limits).
export function getSentryTraceHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  safe('getSentryTraceHeaders', () => {
    const span = Sentry.getActiveSpan();
    if (!span) return;
    const traceHeader = Sentry.spanToTraceHeader(span);
    const baggageHeader = Sentry.spanToBaggageHeader(span);
    if (typeof traceHeader === 'string' && traceHeader.length < 500) {
      headers['sentry-trace'] = traceHeader;
    }
    if (typeof baggageHeader === 'string' && baggageHeader.length < 2000) {
      headers.baggage = baggageHeader;
    }
  });
  return headers;
}

function getEnvironment(): 'ci' | 'development' | 'staging' | 'production' {
  if (import.meta.env.VITE_SENTRY_ENVIRONMENT === 'ci') return 'ci';
  if (typeof window === 'undefined') return 'production';
  const hostname = window.location.hostname;
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.local')
  ) {
    return 'development';
  }
  if (
    hostname.includes('staging') ||
    hostname.includes('stage') ||
    // entr.dev.theartisan.ai — must not pollute the production environment
    // (matches the B2C frontend's convention: dev-server hosts → staging).
    hostname.includes('dev.')
  ) {
    return 'staging';
  }
  return 'production';
}

function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  try {
    event.tags = {
      ...event.tags,
      app: 'entr-kiosk',
    };

    if (event.request?.url) {
      event.request.url = sanitizeUrlForTelemetry(event.request.url);
    }

    if (event.request?.headers) {
      event.request.headers = sanitizeForTelemetry(
        event.request.headers
      ) as Record<string, string>;
    }

    if (event.request?.data) {
      event.request.data = sanitizeForTelemetry(event.request.data);
    }

    if (event.extra) {
      event.extra = sanitizeForTelemetry(event.extra) as Record<
        string,
        unknown
      >;
    }

    if (event.contexts) {
      event.contexts = sanitizeForTelemetry(event.contexts) as Sentry.Contexts;
    }

    return event;
  } catch (error) {
    console.warn('[Sentry] beforeSend failed:', error);
    return event;
  }
}

function beforeBreadcrumb(
  breadcrumb: Sentry.Breadcrumb
): Sentry.Breadcrumb | null {
  try {
    if (breadcrumb.data) {
      breadcrumb.data = sanitizeForTelemetry(breadcrumb.data) as Record<
        string,
        unknown
      >;
    }
    return breadcrumb;
  } catch {
    return breadcrumb;
  }
}

function safe<T>(label: string, callback: () => T): T | undefined {
  if (!initialized && !label.startsWith('setTag:app')) return undefined;
  try {
    return callback();
  } catch (error) {
    console.warn(`[Sentry] ${label} failed:`, error);
    return undefined;
  }
}
